<template>
  <v-row>
    <v-col cols="12">
      <v-card class="mt-5">
        <v-card-title
          >Hallituksen tiedostopankki
          <v-spacer />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="12" md="12">
              <span class="text-subtitle-1">Viimeisimmät tiedostot</span>
              <template v-if="loading">
                <v-skeleton-loader
                  v-if="loading"
                  type="list-item@5"
                ></v-skeleton-loader>
              </template>
              <v-data-table
                :headers="latestFilesHeaders"
                :items="latestFiles"
                :items-per-page="-1"
                hide-default-footer
                class="elevation-1 mt-2 cursor-pointer-table"
                @click:row="openFile"
              >
                <template v-slot:item.icon="{ item }">
                  <v-progress-circular
                    v-if="item._opening"
                    indeterminate
                    size="24"
                    color="primary"
                  ></v-progress-circular>
                  <v-icon v-else
                    >mdi {{ getFileIcon(item.fileExtension) }}</v-icon
                  >
                </template>
                <template v-slot:item.createdAt="{ item }">
                  {{ $formatDateAndHourMinute(item.createdAt) }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text>
          <v-divider class="mt-5 mb-5" />
          <v-row>
            <v-col cols="12" md="12">
              <v-text-field
                v-model="search"
                label="Hae tiedostoista"
                flat
                hide-details
                clearable
              >
                <template v-slot:append-outer>
                  <v-btn icon @click="additionalFilters = !additionalFilters"
                    ><v-icon>mdi mdi-filter</v-icon></v-btn
                  >
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="additionalFilters">
            <v-col cols="12">
              <v-row>
                <v-col cols="12">
                  <span class="text-subtitle-2"> Lisäysvuosi</span>
                  <v-chip-group column v-model="filters.year" multiple>
                    <v-chip
                      v-for="year in years"
                      :key="year"
                      :value="year"
                      class="mr-2"
                      outlined
                      color="primary"
                      >{{ year }}</v-chip
                    >
                  </v-chip-group>
                </v-col>
              </v-row>
              <v-divider />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-title>
          <v-btn-toggle v-model="viewMode" mandatory rounded dense class="mr-2">
            <v-btn value="tree" small>
              <v-icon small>mdi-file-tree</v-icon>
            </v-btn>
            <v-btn value="explorer" small>
              <v-icon small>mdi-folder-table</v-icon>
            </v-btn>
          </v-btn-toggle>
          <v-btn
            v-if="viewMode === 'tree'"
            :disabled="loading"
            @click="
              () => {
                $refs.treeview.updateAll(updateAll);
                updateAll = !updateAll;
              }
            "
            class="mr-2"
            elevation="0"
            rounded
            small
            outlined
            color="primary"
          >
            <v-icon
              >mdi
              {{
                updateAll
                  ? "mdi-unfold-more-horizontal"
                  : "mdi-unfold-less-horizontal"
              }}</v-icon
            >
            {{ updateAll ? "Avaa kaikki" : "Sulje kaikki" }}
          </v-btn>
          <v-btn
            @click="showFullDescription = !showFullDescription"
            class="mr-2"
            elevation="0"
            rounded
            small
            outlined
            :color="showFullDescription ? 'primary' : ''"
          >
            <v-icon left>mdi mdi-text-box-outline</v-icon>
            {{ showFullDescription ? "Tiivistä kuvaus" : "Näytä koko kuvaus" }}
          </v-btn>
          <v-btn
            @click="showDepartments = !showDepartments"
            class="mr-2"
            elevation="0"
            rounded
            small
            outlined
            :color="showDepartments ? 'primary' : ''"
          >
            <v-icon left>mdi mdi-label</v-icon>
            {{ showDepartments ? "Piilota osastot" : "Näytä osastot" }}
          </v-btn>
          <v-spacer />
          <div>
            <v-btn
              color="primary"
              rounded
              small
              v-if="editMode"
              class="mr-2"
              @click="
                () => {
                  addModal.category.parentCategoryId = null;
                  addModal.file.categoryId = null;
                  addModalType = 'category';
                  showAddModal = true;
                }
              "
            >
              <v-icon>mdi mdi-plus</v-icon> Uusi kansio
            </v-btn>
            <v-btn
              color="primary"
              rounded
              small
              @click="editMode = !editMode"
              v-if="hasEditPermissions"
            >
              <v-icon left v-if="!editMode">mdi mdi-pencil</v-icon>
              <v-icon left v-if="editMode">mdi mdi-pencil-off</v-icon>
              {{ editMode ? "Lopeta muokkaus" : "Muokkaa" }}
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="mb-10">
          <template v-if="loading && categories.length === 0">
            <v-skeleton-loader type="list-item@3"></v-skeleton-loader>
          </template>
          <v-treeview
            v-if="viewMode === 'tree'"
            :items="items"
            open-on-click
            ref="treeview"
            :activatable="false"
            :search="search"
          >
            <template v-slot:prepend="{ item, open }">
              <v-icon v-if="item.type === 'category'"
                >mdi {{ open ? "mdi-folder-open" : "mdi-folder" }}
              </v-icon>
              <v-icon v-if="item.type === 'file'"
                >mdi {{ getFileIcon(item.fileExtension) }}
              </v-icon>
            </template>
            <template v-slot:label="{ item, open }">
              <tiedostopankki-item
                :edit-mode="editMode"
                :item="item"
                :open="open"
                :open-file="() => openFile(item)"
              />
            </template>
            <template v-slot:append="{ item }" v-if="editMode">
              <v-btn
                icon
                @click.stop="openEditFileModal(item)"
                v-if="item.type === 'file'"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                @click.stop="confirmDeleteCategory(item)"
                v-if="item.type === 'category' && hasDeletePermission(item)"
                class="error--text"
                :loading="item._loadingCategoryDelete"
              >
                <v-icon>mdi mdi-close</v-icon>
              </v-btn>
              <v-btn
                icon
                @click.stop="confirmDeleteFile(item)"
                v-if="item.type === 'file' && hasDeletePermission(item)"
                class="mr-9 error--text"
                :loading="item._loadingFileDelete"
              >
                <v-icon>mdi mdi-close</v-icon>
              </v-btn>
              <v-btn
                icon
                class=""
                @click.stop="
                  () => {
                    addModal.category.parentCategoryId = item.id;
                    addModal.file.categoryId = item.id;
                    addModalType = 'category';
                    showAddModal = true;
                  }
                "
                v-if="item.type === 'category'"
              >
                <v-icon>mdi mdi-plus</v-icon>
              </v-btn>
            </template>
          </v-treeview>
          <tiedostopankki-explorer
            v-if="viewMode === 'explorer'"
            :categories="categories"
            :files="files"
            :edit-mode="editMode"
            :loading="loading"
            :search="search"
            :show-departments="showDepartments"
            :show-full-description="showFullDescription"
            @open-file="openFile"
            @delete-file="confirmDeleteFile"
            @delete-category="confirmDeleteCategory"
            @edit-file="openEditFileModal"
            @add-category="
              (parentId) => {
                addModal.category.parentCategoryId = parentId;
                addModalType = 'category';
                showAddModal = true;
              }
            "
            @add-file="
              (catId) => {
                addModal.file.categoryId = catId;
                addModalType = 'file';
                showAddModal = true;
              }
            "
          />
        </v-card-text>
      </v-card>
      <v-dialog v-model="showAddModal" max-width="600" persistent>
        <v-form ref="form">
          <v-card>
            <v-card-title>
              <h3>
                Lisää
                <span v-if="addModalType === 'file'">tiedosto</span>
                <span v-if="addModalType === 'category'">kansio</span>
              </h3>
              <v-spacer />
              <v-btn icon @click="showAddModal = false">
                <v-icon>mdi mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="mt-2">
              <span class="text-subtitle-1"
                ><strong>Yläkansio:</strong>
                {{ addModalParentCategories || "Ei yläkansiota" }}</span
              >
            </v-card-text>

            <template v-if="addModalType === 'category'">
              <v-card-title>
                <h5>Uusi kansio</h5>
              </v-card-title>
              <v-card-text>
                <v-text-field
                  label="Nimi"
                  v-model="addModal.category.name"
                  outlined
                ></v-text-field>
                <!--<v-text-field
                  label="Kuvaus"
                  v-model="addModal.category.description"
                  outlined
                ></v-text-field>-->
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="addModal.category.departments"
                      :items="departments"
                      multiple
                      item-text="label"
                      item-value="value"
                      label="Osastot"
                      outlined
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  @click="saveCategory"
                  :loading="loadingCategorySave"
                >
                  Tallenna kansio
                </v-btn>
              </v-card-actions>
            </template>

            <template v-if="addModalType === 'file'">
              <template v-if="addModal.file.categoryId">
                <v-divider></v-divider>
                <v-card-title>
                  <h5>Uusi tiedosto</h5>
                </v-card-title>
                <v-card-text>
                  <file-upload
                    ref="upload"
                    :multiple="true"
                    @update:files="changeAddModalFileNameIfEmpty"
                  ></file-upload>
                  <!--<v-text-field
                    label="Nimi"
                    v-model="addModal.file.name"
                    outlined
                  ></v-text-field>-->
                  <v-row>
                    <v-col cols="12">
                      <v-select
                        v-model="addModal.file.departments"
                        :items="departments"
                        multiple
                        item-text="label"
                        item-value="value"
                        label="Osastot"
                        outlined
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    @click="saveFiles"
                    :loading="loadingFileSave"
                  >
                    Tallenna tiedosto
                  </v-btn>
                </v-card-actions>
              </template>
            </template>
          </v-card></v-form
        >
      </v-dialog>

      <v-dialog v-model="editFileModal.show" max-width="600" persistent>
        <v-card>
          <v-card-title>
            <h3>Muokkaa tiedostoa</h3>
            <v-spacer />
            <v-btn icon @click="editFileModal.show = false">
              <v-icon>mdi mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="mt-4">
            <v-text-field
              label="Alkuperäinen tiedostonimi"
              v-model="editFileModal.filename"
              outlined
              disabled
            ></v-text-field>
            <v-text-field
              label="Oma tiedostonimi (näkyy listauksessa)"
              v-model="editFileModal.customFilename"
              outlined
              :suffix="'.' + editFileModal.fileExtension"
            ></v-text-field>
            <v-textarea
              label="Kuvaus"
              v-model="editFileModal.description"
              outlined
              rows="3"
            ></v-textarea>
            <v-select
              v-model="editFileModal.departments"
              :items="departments"
              multiple
              item-text="label"
              item-value="value"
              label="Osastot"
              outlined
            ></v-select>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="updateFile"
              :loading="editFileModal.loading"
            >
              Tallenna muutokset
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import dayjs from "dayjs";
import api from "@/api";
import FileUpload from "@/components/FileUpload.vue";
import { getBlob, ref, uploadBytes } from "firebase/storage";
import downloadjs from "downloadjs";
import { orderBy } from "lodash";
import TiedostopankkiItem from "@/components/TiedostopankkiItem.vue";
import { tiedostopankkiHallitusStorage } from "@/plugins/firebase.app";
import TiedostopankkiExplorer from "@/components/TiedostopankkiExplorer.vue";

export default {
  name: "TiedostopankkiHallitus",
  components: { TiedostopankkiExplorer, TiedostopankkiItem, FileUpload },
  data: () => {
    return {
      viewMode: "explorer",
      loading: false,
      categories: [],
      files: [],
      showAddModal: false,
      editMode: false,
      updateAll: true,
      search: null,
      showDepartments: false,
      showFullDescription: false,
      additionalFilters: false,
      editFileModal: {
        show: false,
        loading: false,
        id: null,
        filename: "",
        customFilename: "",
        description: "",
        departments: [],
        fileExtension: "",
      },
      addModal: {
        category: {
          name: "",
          description: "",
          parentCategoryId: null,
          departments: ["hallitus"],
        },
        file: {
          name: "",
          categoryId: null,
          description: "",
          departments: ["hallitus"],
        },
      },
      loadingFileSave: false,
      loadingCategorySave: false,
      addModalType: "category", // 'category' or 'file'
      filters: {
        year: null,
      },
      latestFilesHeaders: [
        { text: "", value: "icon", sortable: false, width: "50px" },
        { text: "Nimi", value: "filename" },
        { text: "Kansio", value: "_categoryTreeAsString" },
        { text: "Lisätty", value: "createdAt" },
      ],
    };
  },
  computed: {
    latestFiles() {
      const files = [...orderBy(this.files, "createdAt", "desc").slice(0, 5)];
      files.map((f) => {
        f._categoryTree = this.getParentTreeForCategory(f.categoryId);
        f._categoryTreeAsString = f._categoryTree
          .map((c) => c.name)
          .join(" / ");
        return f;
      });
      return files;
    },
    today() {
      return dayjs().format("LLLL");
    },
    years() {
      return new Set([
        ...this.files.map((f) => dayjs(f.createdAt).format("YYYY")),
        "2023",
      ]);
    },
    items() {
      return this.categories
        .filter((c) => !c.parentCategoryId)
        .flatMap((category) => {
          return this.getCategory(category);
        })
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
    },
    addModalParentCategories() {
      let parentId = null;
      if (this.addModalType === "category") {
        parentId = this.addModal.category.parentCategoryId;
      } else if (this.addModalType === "file") {
        parentId = this.addModal.file.categoryId;
      }

      if (parentId) {
        return this.getParentTreeForCategory(parentId)
          .map((c) => c.name)
          .join(" / ");
      }
      return "";
    },

    departments() {
      return [
        {
          value: "hallitus",
          label: "Hallitus",
          disabled: !this.$hasRole("hallitus"),
        },
        {
          value: "tyosuojelutoimikunta",
          label: "Työsuojelutoimikunta",
          disabled: !this.$isTyosuojelutoimikunta,
        },
      ];
    },
    hasEditPermissions() {
      return this.$isSuperAdmin;
    },
  },
  methods: {
    async reloadAll() {
      this.loading = true;
      try {
        const result = (await api.tiedostopankkiHallitusGetFilesAndCategories())
          .data;
        this.categories = result.categories.map((c) => {
          c._loadingCategoryDelete = false;
          return c;
        });
        this.files = result.files.map((file) => {
          return {
            ...file,
            createdAtDayJs: dayjs(file.createdAt),
            _loadingFileDelete: false,
          };
        });
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    getCategory(category) {
      (category.type = "category"),
        (category.children = [
          ...this.categories
            .filter((c) => c.parentCategoryId === category.id)
            .flatMap((c) => this.getCategory(c))
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: "base",
              })
            ),

          ...this.files
            .filter((file) => file.categoryId === category.id)
            .filter((file) => {
              let r = true;
              if (this.filters.year) {
                r = this.filters.year.some(
                  (y) => y === file.createdAtDayJs.format("YYYY")
                );
              }
              return r;
            })
            .map((file) => {
              file.type = "file";
              file.name = file.filename;
              return file;
            })
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: "base",
              })
            ),
        ]);
      return category;
    },
    async confirmDeleteFile(file) {
      if (
        await this.$confirm("Haluatko varmasti poistaa tiedoston?", {
          buttonTrueText: "Poista",
          buttonFalseText: "Peruuta",
          color: "error",
          title: file.filename,
        })
      ) {
        file._loadingFileDelete = true;
        try {
          await api.tiedostopankkiHallitusDeleteFile({
            key: file.key,
            id: file.id,
          });
          await this.reloadAll();
          this.$toast("Tiedosto poistettu", {
            type: "success",
          });
        } catch (e) {
          console.error(e);
          this.$toast("Tiedoston poistaminen epäonnistui", {
            type: "error",
          });
          file._loadingFileDelete = false;
        }
      }
    },
    async confirmDeleteCategory(category) {
      if (
        await this.$confirm(
          "Haluatko varmasti poistaa kansion? Myös kaikki kansion tiedostot poistetaan.",
          {
            buttonTrueText: "Poista",
            buttonFalseText: "Peruuta",
            color: "error",
            title: category.name,
          }
        )
      ) {
        category._loadingCategoryDelete = true;
        try {
          await api.tiedostopankkiHallitusDeleteCategory({
            id: category.id,
          });
          await this.reloadAll();
          this.$toast("Kansio poistettu", {
            type: "success",
          });
        } catch (e) {
          console.error(e);
          this.$toast("Kansion poistaminen epäonnistui", {
            type: "error",
          });
          category._loadingCategoryDelete = false;
        }
      }
    },
    hasDeletePermission(file) {
      return this.$isHallitus;
      /*return (
        this.$isAdmin ||
        (file.departments.includes("naisosasto") &&
          file.departments.length === 1 &&
          this.$isNaisosasto)
      );*/
    },
    getFileIcon(extension) {
      const icons = {
        pdf: "mdi-file-document",
        jpg: "mdi-file-image",
        jpeg: "mdi-file-image",
        png: "mdi-file-image",
        gif: "mdi-file-image",
        bmp: "mdi-file-image",
        mp3: "mdi-file-music",
        wav: "mdi-file-music",
        mp4: "mdi-file-video",
        mov: "mdi-file-video",
        avi: "mdi-file-video",
        doc: "mdi-file-word",
        docx: "mdi-file-word",
        xls: "mdi-file-excel",
        xlsx: "mdi-file-excel",
        ppt: "mdi-file-powerpoint",
        pptx: "mdi-file-powerpoint",
        // Add more file types and icons as needed
      };

      // Return the icon name if found; otherwise, return a default file icon
      return icons[extension.toLowerCase()] || "mdi-file";
    },
    getParentTreeForCategory(id) {
      const parent = this.categories.find((c) => c.id === id);
      if (parent) {
        return this.getParentTreeForCategory(parent.parentCategoryId).concat(
          parent
        );
      }
      return [];
    },
    async openFile(file) {
      this.$set(file, "_opening", true);
      try {
        const theBlob = await getBlob(
          ref(tiedostopankkiHallitusStorage, file.key)
        );
        downloadjs(theBlob, file.filename, file.fileType);
      } catch (error) {
        console.error(error);
        this.$toast("Tiedostoa ei voitu avata. Yritä uudelleen.", {
          type: "error",
        });
      }
      this.$set(file, "_opening", false);
    },
    async saveCategory() {
      this.loadingCategorySave = true;

      try {
        await api.tiedostopankkiHallitusAddCategory({
          name: this.addModal.category.name,
          description: this.addModal.category.description || "",
          parentCategoryId: this.addModal.category.parentCategoryId,
          departments: this.addModal.category.departments,
        });
        this.$toast("Kansio tallennettu.", {
          type: "success",
        });
        this.addModal.category.name = "";
        this.addModal.category.description = "";

        this.reloadAll();
      } catch (e) {
        this.$toast("Tallennus epäonnistui. Ole hyvä ja yritä uudelleen.", {
          type: "error",
        });
        console.error(e);
      }

      this.loadingCategorySave = false;
    },
    async saveFiles() {
      this.loadingFileSave = true;
      if (!this.$refs.form.validate()) {
        return;
      }
      if (!this.addModal.file.categoryId) {
        this.$toast("Ei kansiota. Kansio tulee olla valittu.", {
          type: "error",
        });
        return;
      }
      const files = await this.$refs.upload?.getRawFiles();

      try {
        if (files) {
          for (let file of files) {
            const uniqueId =
              Date.now().toString(36) + Math.random().toString(36).substring(2);
            const key = this.addModal.file.categoryId + "/" + uniqueId;
            const storageRef = ref(tiedostopankkiHallitusStorage, key);

            await uploadBytes(storageRef, file);

            await api.tiedostopankkiHallitusAddFile({
              categoryId: this.addModal.file.categoryId,
              description: this.addModal.file.description || "",
              departments: this.addModal.file.departments,
              filename: file.name,
              fileType: file.type,
              fileSize: file.size,
              fileExtension: file.name.split(".").pop(),
              key: key,
            });
          }
        }

        this.$toast("Tiedosto tallennettu.", {
          type: "success",
        });
        this.addModal.file.description = "";
        this.$refs.upload?.reset();
        this.reloadAll();
      } catch (e) {
        this.$toast("Tallennus epäonnistui. Ole hyvä ja yritä uudelleen.", {
          type: "error",
        });
        console.error(e);
      }
      this.loadingFileSave = false;
    },
    changeAddModalFileNameIfEmpty() {
      if (this.addModal.file.name === "" && this.$refs.upload?.files?.[0]) {
        this.addModal.file.name = this.$refs.upload.files?.[0]?.filename;
      }
    },
    openEditFileModal(file) {
      this.editFileModal = {
        show: true,
        loading: false,
        id: file.id,
        filename: file.filename,
        customFilename: file.customFilename || "",
        description: file.description || "",
        departments: file.departments || [],
        fileExtension: file.fileExtension,
      };
    },
    async updateFile() {
      this.editFileModal.loading = true;
      try {
        await api.tiedostopankkiHallitusUpdateFile({
          id: this.editFileModal.id,
          customFilename: this.editFileModal.customFilename,
          description: this.editFileModal.description,
          departments: this.editFileModal.departments,
        });
        this.$toast("Tiedosto päivitetty", { type: "success" });
        this.editFileModal.show = false;
        await this.reloadAll();
      } catch (e) {
        console.error(e);
        this.$toast("Virhe tiedoston päivityksessä", { type: "error" });
      }
      this.editFileModal.loading = false;
    },
  },
  mounted() {
    this.reloadAll();
  },
  watch: {
    showAddModal() {
      if (!this.showAddModal) {
        this.$refs.form.reset();
      }

      const parentCategory = this.categories.find(
        (c) => c.id === this.addModal.category.parentCategoryId
      );
      if (parentCategory) {
        this.addModal.category.departments = parentCategory.departments;
        this.addModal.file.departments = parentCategory.departments;
      } else {
        this.addModal.category.departments = [
          this.$isHallitus && "hallitus",
        ].filter(Boolean);
        this.addModal.file.departments = [
          this.$isHallitus && "hallitus",
        ].filter(Boolean);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.cursor-pointer-table {
  ::v-deep tbody tr {
    cursor: pointer;
  }
}
</style>
